// src/services/api/AchievementsApi.tsx
import { BaseAPI } from "./BaseApi";
import type {
  AchievementStatsResponse,
  UserGameAchievementsResponse,
  SyncResponse,
  UserGamesResponse,
} from "../../types/achievements";

/**
 * AchievementsAPI Client
 * 
 * Provides methods to interact with achievement endpoints.
 * All methods include authentication credentials.
 * 
 */
export class AchievementsAPI extends BaseAPI {
  constructor() {
    super(""); // uses same base URL as other API classes
  }

  /**
   * Fetch user achievement statistics
   * GET /api/users/:userId/achievements/stats
   * 
   * 
   * @param userId - The user's UUID
   * @returns Promise resolving to achievement statistics
   * @throws ApiError on HTTP errors (401, 403, 404, 500)
   */
  async getUserAchievementStats(userId: string): Promise<AchievementStatsResponse> {
    return this.request<AchievementStatsResponse>(
      `/api/users/${userId}/achievements/stats`,
      {
        method: "GET",
        auth: true,
      }
    );
  }

  /**
   * Fetch user achievements for a specific game
   * GET /api/users/:userId/games/:gameId/achievements
   * 
   * 
   * @param userId - The user's UUID
   * @param gameId - The game's ID
   * @returns Promise resolving to user game achievements
   * @throws ApiError on HTTP errors (401, 403, 404, 500)
   */
  async getUserGameAchievements(
    userId: string,
    gameId: string
  ): Promise<UserGameAchievementsResponse> {
    return this.request<UserGameAchievementsResponse>(
      `/api/users/${userId}/games/${gameId}/achievements`,
      {
        method: "GET",
        auth: true,
      }
    );
  }

  /**
   * Trigger achievement sync for a user
   * POST /api/users/:userId/achievements/sync
   * 
   * 
   * @param userId - The user's UUID
   * @returns Promise resolving to sync operation result
   * @throws ApiError on HTTP errors (401, 403, 429, 500)
   */
  async syncUserAchievements(userId: string): Promise<SyncResponse> {
    return this.request<SyncResponse>(
      `/api/users/${userId}/achievements/sync`,
      {
        method: "POST",
        auth: true,
      }
    );
  }

  /**
   * Fetch user games with details including names
   * GET /api/users/get/games/:userId
   * 
   * 
   * @param userId - The user's UUID
   * @returns Promise resolving to user games with details
   * @throws ApiError on HTTP errors (401, 403, 404, 500)
   */
  async getUserGames(userId: string): Promise<UserGamesResponse> {
    return this.request<UserGamesResponse>(
      `/api/users/get/games/${userId}`,
      {
        method: "GET",
        auth: true,
      }
    );
  }
}
